import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Edit2, Save, X, Sparkles, CheckCircle, Plus, Trash2, Copy, FolderPlus, Pen, FolderX, Eye, Upload, Image, Power, PowerOff } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { apiClient } from '@/services/api/apiClient';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { MenuSearch, SearchFilters } from '@/components/MenuSearch';

interface MenuItem {
  menu_item_id: number;
  item_name: string;
  item_description: string;
  primary_price: number;
  section: string;
  dietary_flags?: any;
  is_beverage?: boolean;
  is_seasonal?: boolean;
  seasonal_menu_id?: number;
  uploaded_image_url?: string | null;
  ai_image_url?: string | null;
  is_market_price?: boolean;
  market_price_indicator?: string;
  availability?: string | null;
  is_popular?: boolean;
  chef_reccom?: boolean;
  is_hero_carousel?: boolean;
  hero_display_order?: number | null;
}

interface MenuSection {
  section_name: string;
  items: MenuItem[];
}

const SeasonalMenuItems = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const [menuItems, setMenuItems] = useState<MenuSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({
    sections: [],
    dietary: [],
    itemType: 'all',
    priceRange: {}
  });
  const [editingItems, setEditingItems] = useState<Set<number>>(new Set());
  const [menuName, setMenuName] = useState('');
  const [editingMenuName, setEditingMenuName] = useState(false);
  const [tempMenuName, setTempMenuName] = useState('');
  const [saving, setSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [deletingItems, setDeletingItems] = useState<Set<number>>(new Set());
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [creatingSection, setCreatingSection] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');
  const [editingSections, setEditingSections] = useState<Record<string, string>>({});
  const [savingSections, setSavingSections] = useState<Set<string>>(new Set());
  const [showItemPreview, setShowItemPreview] = useState<number | null>(null);
  const [itemToDelete, setItemToDelete] = useState<{id: number, name: string} | null>(null);
  const [imageToDelete, setImageToDelete] = useState<{id: number, name: string} | null>(null);
  const [uploadingImages, setUploadingImages] = useState<Set<number>>(new Set());
  const [deletingImages, setDeletingImages] = useState<Set<number>>(new Set());
  const [togglingAvailability, setTogglingAvailability] = useState<Set<number>>(new Set());
  const [newItem, setNewItem] = useState({
    item_name: '',
    item_description: '',
    primary_price: '',
    section: '',
    is_popular: false,
    chef_reccom: false,
    is_hero_carousel: false,
    hero_display_order: null as number | null
  });

  useEffect(() => {
    fetchMenuItems();
    fetchSeasonalMenu();
  }, [id]);

  const fetchSeasonalMenu = async () => {
    try {
      const response = await apiClient.get(`/seasonal-menus/${id}`);
      setMenuName(response.data.menu_name);
    } catch (error) {
      console.error('Error fetching seasonal menu:', error);
    }
  };

  const fetchMenuItems = async () => {
    try {
      setLoading(true);
      
      // Fetch seasonal menu items
      const response = await apiClient.get(`/seasonal-menus/${id}/items`);
      const data = response.data;
      
      // Group items by section
      const itemsBySection: { [key: string]: MenuItem[] } = {};
      data.items.forEach((item: MenuItem) => {
        const section = item.section || 'Uncategorized';
        if (!itemsBySection[section]) {
          itemsBySection[section] = [];
        }
        itemsBySection[section].push(item);
      });

      // Convert to sections array
      const sections = Object.entries(itemsBySection).map(([section_name, items]) => ({
        section_name,
        items
      }));

      setMenuItems(sections);
      
      // Set first section as selected if none selected
      if (sections.length > 0 && !selectedSectionId) {
        setSelectedSectionId(sections[0].section_name);
      }
    } catch (error) {
      console.error('Error fetching menu items:', error);
      toast({
        title: 'Error',
        description: 'Failed to load menu items',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (itemId: number) => {
    setEditingItems(new Set([...editingItems, itemId]));
  };

  const handleCancel = (itemId: number) => {
    const newEditing = new Set(editingItems);
    newEditing.delete(itemId);
    setEditingItems(newEditing);
    // Refresh to get original data
    fetchMenuItems();
  };

  const handleSave = async (item: MenuItem) => {
    try {
      const originalSection = selectedSectionId;
      const newSection = item.section;

      await apiClient.put(`/seasonal-menus/${id}/items/${item.menu_item_id}`, {
        name: item.item_name,
        description: item.item_description,
        price: item.primary_price,
        section: item.section, // Include section for re-categorization
        is_popular: item.is_popular || false,
        chef_reccom: item.chef_reccom || false,
        is_hero_carousel: item.is_hero_carousel || false,
        hero_display_order: item.hero_display_order || null
      });

      const newEditing = new Set(editingItems);
      newEditing.delete(item.menu_item_id);
      setEditingItems(newEditing);

      // Refresh menu items to reflect section changes
      await fetchMenuItems();

      // If item moved to a different section, switch to that section
      if (originalSection !== newSection) {
        setSelectedSectionId(newSection);
        toast({
          title: 'Saved',
          description: `Item moved to "${newSection}" section`
        });
      } else {
        toast({
          title: 'Saved',
          description: 'Item updated successfully'
        });
      }
    } catch (error) {
      console.error('Error saving item:', error);
      toast({
        title: 'Error',
        description: 'Failed to save changes',
        variant: 'destructive'
      });
    }
  };

  const handleFieldChange = (sectionIndex: number, itemIndex: number, field: string, value: any) => {
    const newSections = [...menuItems];
    newSections[sectionIndex].items[itemIndex] = {
      ...newSections[sectionIndex].items[itemIndex],
      [field]: value
    };
    setMenuItems(newSections);
  };

  const handleDeleteItem = (itemId: number) => {
    const item = menuItems.flatMap(s => s.items).find(i => i.menu_item_id === itemId);
    if (item) {
      setItemToDelete({ id: itemId, name: item.item_name });
    }
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    const itemId = itemToDelete.id;
    setDeletingItems(new Set([...deletingItems, itemId]));

    try {
      await apiClient.delete(`/seasonal-menus/${id}/items/${itemId}`);

      toast({
        title: 'Success',
        description: `"${itemToDelete.name}" has been deleted successfully`
      });

      // Refresh items
      fetchMenuItems();
      setItemToDelete(null);
    } catch (error) {
      console.error('Error deleting item:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete item',
        variant: 'destructive'
      });
    } finally {
      const newDeleting = new Set(deletingItems);
      newDeleting.delete(itemId);
      setDeletingItems(newDeleting);
    }
  };

  const handleImageUpload = async (itemId: number, file: File) => {
    // Add to uploading set
    setUploadingImages(new Set([...uploadingImages, itemId]));

    try {
      const formData = new FormData();
      formData.append('file', file);

      await apiClient.post(`/seasonal-menus/${id}/items/${itemId}/upload-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast({
        title: 'Success',
        description: 'Image uploaded successfully'
      });

      // Refresh items to show the new image
      await fetchMenuItems();
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: 'Error',
        description: 'Failed to upload image',
        variant: 'destructive'
      });
    } finally {
      // Remove from uploading set
      const newUploading = new Set(uploadingImages);
      newUploading.delete(itemId);
      setUploadingImages(newUploading);
    }
  };

  const handleDeleteImage = (itemId: number) => {
    const item = menuItems.flatMap(s => s.items).find(i => i.menu_item_id === itemId);
    if (item) {
      setImageToDelete({ id: itemId, name: item.item_name });
    }
  };

  const confirmDeleteImage = async () => {
    if (!imageToDelete) return;

    const itemId = imageToDelete.id;
    setDeletingImages(new Set([...deletingImages, itemId]));

    try {
      await apiClient.delete(`/seasonal-menus/${id}/items/${itemId}/image`);

      toast({
        title: 'Success',
        description: 'Image deleted successfully'
      });

      // Refresh items to remove the image
      await fetchMenuItems();
      setImageToDelete(null);
    } catch (error) {
      console.error('Error deleting image:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete image',
        variant: 'destructive'
      });
    } finally {
      // Remove from deleting set
      const newDeleting = new Set(deletingImages);
      newDeleting.delete(itemId);
      setDeletingImages(newDeleting);
    }
  };

  const handleToggleAvailability = async (itemId: number, currentAvailability: string | null) => {
    setTogglingAvailability(new Set([...togglingAvailability, itemId]));

    try {
      const newAvailability = currentAvailability === 'inactive' ? null : 'inactive';

      await apiClient.patch(`/seasonal-menus/${id}/items/${itemId}/availability`, {
        availability: newAvailability
      });

      const statusText = newAvailability === 'inactive' ? 'disabled (out of stock)' : 'enabled (back in stock)';

      toast({
        title: 'Success',
        description: `Item ${statusText}`
      });

      // Refresh items to show updated availability
      await fetchMenuItems();
    } catch (error) {
      console.error('Error toggling availability:', error);
      toast({
        title: 'Error',
        description: 'Failed to update item availability',
        variant: 'destructive'
      });
    } finally {
      // Remove from toggling set
      const newToggling = new Set(togglingAvailability);
      newToggling.delete(itemId);
      setTogglingAvailability(newToggling);
    }
  };

  const handleAddItem = async () => {
    if (!newItem.item_name || !newItem.primary_price || !newItem.section) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields',
        variant: 'destructive'
      });
      return;
    }

    try {
      await apiClient.post(`/seasonal-menus/${id}/items`, {
        item_name: newItem.item_name,
        item_description: newItem.item_description,
        primary_price: parseFloat(newItem.primary_price),
        section: newItem.section,
        is_seasonal: true,
        is_popular: newItem.is_popular,
        chef_reccom: newItem.chef_reccom,
        is_hero_carousel: newItem.is_hero_carousel,
        hero_display_order: newItem.hero_display_order
      });

      toast({
        title: 'Success',
        description: 'Item added successfully'
      });

      // Reset form and close modal
      setNewItem({
        item_name: '',
        item_description: '',
        primary_price: '',
        section: ''
      });
      setShowAddModal(false);
      
      // Refresh items
      fetchMenuItems();
    } catch (error) {
      console.error('Error adding item:', error);
      toast({
        title: 'Error',
        description: 'Failed to add item',
        variant: 'destructive'
      });
    }
  };

  // Get unique sections for the dropdown
  const allSections = [...new Set(menuItems.map(section => section.section_name))];

  // Section management functions
  const handleCreateSection = async () => {
    if (!newSectionName.trim()) return;

    try {
      // Call backend to create section
      const response = await apiClient.post(`/seasonal-menus/${id}/sections`, {
        name: newSectionName.trim(),
        category: 'other'
      });

      const result = response.data;

      // Add new section locally
      const newSection: MenuSection = {
        section_name: result.section.name,
        items: []
      };

      setMenuItems([...menuItems, newSection]);
      setSelectedSectionId(result.section.name);
      setCreatingSection(false);
      setNewSectionName('');

      toast({
        title: 'Success',
        description: result.message || `Section "${result.section.name}" created`
      });
    } catch (error: any) {
      console.error('Error creating section:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to create section',
        variant: 'destructive'
      });
    }
  };

  const handleStartEditSection = (sectionId: string, currentName: string) => {
    setEditingSections({ ...editingSections, [sectionId]: currentName });
  };

  const handleSaveSection = async (sectionId: string) => {
    const newName = editingSections[sectionId]?.trim();
    if (!newName || savingSections.has(sectionId)) return;

    // Add to saving set to prevent duplicate requests
    setSavingSections(prev => new Set(prev).add(sectionId));

    try {
      // Call backend API to update section in database
      await apiClient.put(`/seasonal-menus/${id}/sections/${encodeURIComponent(sectionId)}`, {
        name: newName
      });

      // Clear editing state
      const newEditing = { ...editingSections };
      delete newEditing[sectionId];
      setEditingSections(newEditing);

      // Update selected section if needed
      if (selectedSectionId === sectionId) {
        setSelectedSectionId(newName);
      }

      // Refresh menu items from backend to ensure sync
      await fetchMenuItems();

      toast({
        title: 'Success',
        description: 'Section renamed successfully'
      });
    } catch (error) {
      console.error('Error renaming section:', error);
      toast({
        title: 'Error',
        description: 'Failed to rename section',
        variant: 'destructive'
      });
    } finally {
      // Remove from saving set
      setSavingSections(prev => {
        const next = new Set(prev);
        next.delete(sectionId);
        return next;
      });
    }
  };

  const handleCancelEditSection = (sectionId: string) => {
    const newEditing = { ...editingSections };
    delete newEditing[sectionId];
    setEditingSections(newEditing);
  };

  const handleDeleteSection = async (sectionId: string) => {
    const section = menuItems.find(s => s.section_name === sectionId);
    if (!section) return;

    if (section.items.length > 0) {
      toast({
        title: 'Cannot Delete',
        description: `Section "${sectionId}" contains ${section.items.length} items. Please move or delete items first.`,
        variant: 'destructive'
      });
      return;
    }

    if (!confirm(`Delete section "${sectionId}"?`)) return;

    try {
      // Get section_id from menu_section table - use section name as ID for now
      // TODO: Store section_id from backend in state
      await apiClient.delete(`/seasonal-menus/${id}/sections/${encodeURIComponent(sectionId)}`);

      setMenuItems(menuItems.filter(section => section.section_name !== sectionId));

      if (selectedSectionId === sectionId) {
        const remaining = menuItems.filter(s => s.section_name !== sectionId);
        setSelectedSectionId(remaining.length > 0 ? remaining[0].section_name : null);
      }

      toast({
        title: 'Success',
        description: 'Section deleted successfully'
      });
    } catch (error: any) {
      console.error('Error deleting section:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to delete section',
        variant: 'destructive'
      });
    }
  };

  // Handle search
  const handleSearch = useCallback((term: string, filters: SearchFilters) => {
    setSearchTerm(term);
    setSearchFilters(filters);
  }, []);

  // Filter items based on search
  const getFilteredItems = useCallback(() => {
    let allItems: MenuItem[] = [];
    menuItems.forEach(section => {
      allItems = [...allItems, ...section.items];
    });

    let filtered = allItems;

    // Text search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.item_name.toLowerCase().includes(term) ||
        item.item_description?.toLowerCase().includes(term) ||
        item.primary_price.toString().includes(term)
      );
    }

    // Section filter
    if (searchFilters.sections.length > 0) {
      filtered = filtered.filter(item => searchFilters.sections.includes(item.section || ''));
    }

    // Item type filter
    if (searchFilters.itemType !== 'all') {
      filtered = filtered.filter(item => {
        const isBeverage = item.is_beverage || item.section?.toLowerCase().includes('beverage') || 
                          item.section?.toLowerCase().includes('drink') || 
                          item.section?.toLowerCase().includes('cocktail');
        return searchFilters.itemType === 'beverage' ? isBeverage : !isBeverage;
      });
    }

    // Dietary filter
    if (searchFilters.dietary.length > 0) {
      filtered = filtered.filter(item => {
        if (!item.dietary_flags) return false;
        
        // Check if item has ALL selected dietary restrictions
        return searchFilters.dietary.every(dietary => {
          // Handle both object and boolean dietary flags
          if (typeof item.dietary_flags === 'object') {
            return item.dietary_flags[dietary] === true;
          }
          return false;
        });
      });
    }

    return filtered;
  }, [menuItems, searchTerm, searchFilters]);

  const handleFinish = async () => {
    setSaving(true);
    try {
      // Update seasonal menu status to active
      await apiClient.put(`/seasonal-menus/${id}`, {
        is_active: true
      });

      toast({
        title: 'Success!',
        description: 'Your seasonal menu is now active'
      });

      // Navigate back to dashboard
      navigate('/restaurant-dashboard');
    } catch (error) {
      console.error('Error activating menu:', error);
      toast({
        title: 'Error',
        description: 'Failed to activate menu',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleStartEditMenuName = () => {
    setTempMenuName(menuName);
    setEditingMenuName(true);
  };

  const handleSaveMenuName = async () => {
    if (!tempMenuName.trim()) {
      toast({
        title: 'Error',
        description: 'Menu name cannot be empty',
        variant: 'destructive'
      });
      return;
    }

    setSaving(true);
    try {
      await apiClient.put(`/seasonal-menus/${id}`, {
        menu_name: tempMenuName.trim()
      });

      setMenuName(tempMenuName.trim());
      setEditingMenuName(false);

      toast({
        title: 'Success!',
        description: 'Menu name updated'
      });
    } catch (error) {
      console.error('Error updating menu name:', error);
      toast({
        title: 'Error',
        description: 'Failed to update menu name',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEditMenuName = () => {
    setTempMenuName('');
    setEditingMenuName(false);
  };

  // Get filtered sections based on search
  const filteredItems = getFilteredItems();
  const filteredSections = menuItems.map(section => ({
    ...section,
    items: section.items.filter(item => 
      filteredItems.some(filtered => filtered.menu_item_id === item.menu_item_id)
    )
  })).filter(section => section.items.length > 0);

  // Get selected section
  const selectedSection = menuItems.find(s => s.section_name === selectedSectionId);
  const selectedSectionItems = selectedSection ? 
    selectedSection.items.filter(item => 
      filteredItems.some(filtered => filtered.menu_item_id === item.menu_item_id)
    ) : [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/restaurant-dashboard')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              Back to Dashboard
            </button>

            {/* Editable Menu Name */}
            {editingMenuName ? (
              <div className="flex items-center gap-2">
                <span className="text-lg font-medium text-gray-600">Review</span>
                <Input
                  value={tempMenuName}
                  onChange={(e) => setTempMenuName(e.target.value)}
                  className="bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 h-9 max-w-xs"
                  placeholder="Menu name..."
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveMenuName();
                    if (e.key === 'Escape') handleCancelEditMenuName();
                  }}
                />
                <button
                  onClick={handleSaveMenuName}
                  disabled={saving}
                  className="p-1.5 bg-green-100 hover:bg-green-200 border border-green-300 rounded transition-all disabled:opacity-50"
                  title="Save"
                >
                  <CheckCircle className="h-4 w-4 text-green-600" />
                </button>
                <button
                  onClick={handleCancelEditMenuName}
                  disabled={saving}
                  className="p-1.5 bg-red-100 hover:bg-red-200 border border-red-300 rounded transition-all disabled:opacity-50"
                  title="Cancel"
                >
                  <X className="h-4 w-4 text-red-600" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-semibold text-gray-900">
                  Review {menuName}
                </h1>
                <button
                  onClick={handleStartEditMenuName}
                  className="p-1.5 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded transition-all"
                  title="Rename menu"
                >
                  <Pen className="h-4 w-4 text-gray-500" />
                </button>
              </div>
            )}

            <div className="w-20" /> {/* Spacer for centering */}
          </div>
        </div>
      </div>

      {/* Search Bar and Actions */}
      <div className="container mx-auto px-4 py-6 max-w-6xl space-y-4">
        {/* MenuSearch Component */}
        <MenuSearch
          onSearch={handleSearch}
          sections={menuItems.map(s => ({ id: s.section_name, name: s.section_name }))}
          totalItems={menuItems.reduce((acc, section) => acc + section.items.length, 0)}
          filteredItems={filteredItems.length}
        />

        {/* Removed redundant action buttons - they're now in section headers */}
      </div>

      {/* Main Content - New Layout */}
      <div className="container mx-auto px-4 pb-32">
        <div className="max-w-7xl mx-auto flex gap-6">
          {/* Left Sidebar - Section Navigation */}
          <div className="w-72 flex-shrink-0">
            <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm sticky top-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Menu Sections</h3>
                <button
                  onClick={() => setCreatingSection(true)}
                  className="p-2 bg-green-100 hover:bg-green-200 border border-green-300 rounded-lg transition-all"
                  title="Add New Section"
                >
                  <FolderPlus className="w-4 h-4 text-green-600" />
                </button>
              </div>
              
              {/* Create New Section Form */}
              {creatingSection && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <input
                    type="text"
                    value={newSectionName}
                    onChange={(e) => setNewSectionName(e.target.value)}
                    placeholder="Section name"
                    className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#FDB913] mb-2"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleCreateSection();
                      if (e.key === 'Escape') { setCreatingSection(false); setNewSectionName(''); }
                    }}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleCreateSection}
                      className="flex-1 bg-green-100 hover:bg-green-200 border border-green-300 text-green-700 py-2 px-3 rounded-lg text-sm font-medium transition-all"
                    >
                      Create
                    </button>
                    <button
                      onClick={() => { setCreatingSection(false); setNewSectionName(''); }}
                      className="flex-1 bg-gray-100 hover:bg-gray-200 border border-gray-300 text-gray-600 py-2 px-3 rounded-lg text-sm font-medium transition-all"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              
              {/* Sections List */}
              <div className="space-y-2">
                {filteredSections.map((section) => (
                  <div
                    key={section.section_name}
                    className={`rounded-lg transition-all border relative group ${
                      selectedSectionId === section.section_name
                        ? 'bg-[#FDB913] border-[#FDB913] text-black shadow-sm'
                        : 'bg-gray-50 hover:bg-gray-100 border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                  >
                    {selectedSectionId === section.section_name && (
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-black/20 rounded-l-lg"></div>
                    )}
                    
                    {/* Section Name - Editable */}
                    {editingSections[section.section_name] !== undefined ? (
                      <div className="p-3">
                        <input
                          type="text"
                          value={editingSections[section.section_name]}
                          onChange={(e) => setEditingSections({
                            ...editingSections,
                            [section.section_name]: e.target.value
                          })}
                          className="w-full bg-white border border-gray-200 rounded px-2 py-1 text-gray-900 text-sm mb-2"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveSection(section.section_name);
                            if (e.key === 'Escape') handleCancelEditSection(section.section_name);
                          }}
                        />
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleSaveSection(section.section_name)}
                            disabled={savingSections.has(section.section_name)}
                            className={`p-1 bg-green-100 hover:bg-green-200 border border-green-300 rounded text-green-600 transition-all ${
                              savingSections.has(section.section_name) ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                            title={savingSections.has(section.section_name) ? "Saving..." : "Save section name"}
                          >
                            {savingSections.has(section.section_name) ? (
                              <div className="w-3 h-3 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Save className="w-3 h-3" />
                            )}
                          </button>
                          <button
                            onClick={() => handleCancelEditSection(section.section_name)}
                            disabled={savingSections.has(section.section_name)}
                            className={`p-1 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded text-gray-500 transition-all ${
                              savingSections.has(section.section_name) ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                            title="Cancel editing"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex">
                        <button
                          onClick={() => setSelectedSectionId(section.section_name)}
                          className="flex-1 text-left p-3"
                        >
                          <div className="flex justify-between items-center">
                            <span className="font-medium text-sm">{section.section_name}</span>
                            <span className={`text-xs ${selectedSectionId === section.section_name ? 'text-black/60' : 'text-gray-500'}`}>
                              {section.items.length}
                            </span>
                          </div>
                        </button>

                        {/* Section Controls */}
                        <div className="flex items-center gap-1 pr-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStartEditSection(section.section_name, section.section_name);
                            }}
                            className="p-1 opacity-0 group-hover:opacity-60 hover:!opacity-100 hover:bg-gray-200 rounded transition-all"
                            title="Rename section"
                          >
                            <Pen className="w-3 h-3" />
                          </button>
                          {section.items.length === 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteSection(section.section_name);
                              }}
                              className="p-1 opacity-0 group-hover:opacity-60 hover:!opacity-100 hover:bg-red-100 rounded transition-all text-red-500"
                              title={section.items.length > 0 ? `Delete section and ${section.items.length} items` : "Delete section"}
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Content - Selected Section Only */}
          <div className="flex-1">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#FDB913]"></div>
                <p className="text-gray-500 mt-4">Loading menu items...</p>
              </div>
            ) : !selectedSection ? (
              <div className="text-center py-12">
                <p className="text-gray-500">Select a section to view items</p>
              </div>
            ) : selectedSectionItems.length === 0 ? (
              <motion.div
                key={selectedSection.section_name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  {/* Section Header */}
                  <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                      <div>
                        <h2 className="text-2xl font-semibold text-gray-900">{selectedSection.section_name}</h2>
                        <p className="text-gray-500 text-sm">{selectedSectionItems.length} items</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => navigate(`/seasonal-menu/${id}/copy-from-main`)}
                          className="bg-white text-gray-900 hover:bg-gray-100 border border-gray-200"
                          size="sm"
                        >
                          <Copy className="h-4 w-4 mr-1" />
                          Copy from Main
                        </Button>
                        <Button
                          onClick={() => {
                            setNewItem({ ...newItem, section: selectedSection.section_name });
                            setShowAddModal(true);
                          }}
                          className="bg-[#FDB913] text-black hover:bg-[#e5a711]"
                          size="sm"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add Item
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Empty State */}
                  <div className="p-12 text-center">
                    <p className="text-gray-500 text-lg">No items in this section yet</p>
                    <p className="text-gray-400 text-sm mt-2">Click "Add Item" or "Copy from Main" to get started</p>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key={selectedSection.section_name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  {/* Section Header */}
                  <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                      <div>
                        <h2 className="text-2xl font-semibold text-gray-900">{selectedSection.section_name}</h2>
                        <p className="text-gray-500 text-sm">{selectedSectionItems.length} items</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => navigate(`/seasonal-menu/${id}/copy-from-main`)}
                          className="bg-white text-gray-900 hover:bg-gray-100 border border-gray-200"
                          size="sm"
                        >
                          <Copy className="h-4 w-4 mr-1" />
                          Copy from Main
                        </Button>
                        <Button
                          onClick={() => {
                            setNewItem({ ...newItem, section: selectedSection.section_name });
                            setShowAddModal(true);
                          }}
                          className="bg-[#FDB913] text-black hover:bg-[#e5a711]"
                          size="sm"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add Item
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Items List */}
                  <div className="p-6 space-y-4">
                    {selectedSectionItems.map((item) => {
                      const isEditing = editingItems.has(item.menu_item_id);
                      const sectionIndex = menuItems.findIndex(s => s.section_name === selectedSection.section_name);
                      const itemIndex = selectedSection.items.findIndex(i => i.menu_item_id === item.menu_item_id);
                      
                      return (
                        <motion.div
                          key={item.menu_item_id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          className={`rounded-xl border p-6 hover:bg-gray-50 transition-all ${
                            item.availability === 'inactive'
                              ? 'bg-gray-50 border-orange-300 opacity-60'
                              : 'bg-white border-gray-200'
                          }`}
                        >
                          {isEditing ? (
                            <div className="space-y-4">
                              <Input
                                value={item.item_name}
                                onChange={(e) => handleFieldChange(sectionIndex, itemIndex, 'item_name', e.target.value)}
                                className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#FDB913]"
                                placeholder="Item name"
                              />
                              <Textarea
                                value={item.item_description || ''}
                                onChange={(e) => handleFieldChange(sectionIndex, itemIndex, 'item_description', e.target.value)}
                                className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#FDB913]"
                                placeholder="Description"
                                rows={2}
                              />
                              {/* Section Dropdown */}
                              <div>
                                <label className="text-gray-500 text-sm block mb-1">Section</label>
                                <Select
                                  value={item.section}
                                  onValueChange={(newSection) => {
                                    handleFieldChange(sectionIndex, itemIndex, 'section', newSection);
                                  }}
                                >
                                  <SelectTrigger className="w-full bg-white border-gray-200 text-gray-900">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {allSections.map((section) => (
                                      <SelectItem
                                        key={section}
                                        value={section}
                                      >
                                        {section}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <Input
                                type="number"
                                value={item.primary_price}
                                onChange={(e) => handleFieldChange(sectionIndex, itemIndex, 'primary_price', parseFloat(e.target.value))}
                                className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#FDB913]"
                                placeholder="Price"
                              />

                              {/* Badge and Carousel Options */}
                              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-200">
                                {/* Popular Item Toggle */}
                                <div className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    id={`popular-toggle-${item.menu_item_id}`}
                                    checked={item.is_popular || false}
                                    onChange={(e) => handleFieldChange(sectionIndex, itemIndex, 'is_popular', e.target.checked)}
                                    className="w-4 h-4 text-[#FDB913] bg-white border-gray-300 rounded focus:ring-[#FDB913] focus:ring-2"
                                  />
                                  <label htmlFor={`popular-toggle-${item.menu_item_id}`} className="text-sm text-gray-700">
                                    Popular Item
                                  </label>
                                </div>

                                {/* Chef's Recommendation Toggle */}
                                <div className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    id={`chef-toggle-${item.menu_item_id}`}
                                    checked={item.chef_reccom || false}
                                    onChange={(e) => handleFieldChange(sectionIndex, itemIndex, 'chef_reccom', e.target.checked)}
                                    className="w-4 h-4 text-[#FDB913] bg-white border-gray-300 rounded focus:ring-[#FDB913] focus:ring-2"
                                  />
                                  <label htmlFor={`chef-toggle-${item.menu_item_id}`} className="text-sm text-gray-700">
                                    Chef's Recommendation
                                  </label>
                                </div>

                                {/* Hero Carousel Toggle */}
                                <div className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    id={`hero-toggle-${item.menu_item_id}`}
                                    checked={item.is_hero_carousel || false}
                                    onChange={(e) => handleFieldChange(sectionIndex, itemIndex, 'is_hero_carousel', e.target.checked)}
                                    className="w-4 h-4 text-[#FDB913] bg-white border-gray-300 rounded focus:ring-[#FDB913] focus:ring-2"
                                  />
                                  <label htmlFor={`hero-toggle-${item.menu_item_id}`} className="text-sm text-gray-700">
                                    Hero Carousel (Mobile Landing)
                                  </label>
                                </div>

                                {/* Hero Carousel Display Order - Only show when hero carousel is checked */}
                                {item.is_hero_carousel && (
                                  <div className="col-span-2">
                                    <label className="text-gray-500 text-sm block mb-1">Display Order (1-4)</label>
                                    <Select
                                      value={item.hero_display_order?.toString() || ''}
                                      onValueChange={(value) => handleFieldChange(sectionIndex, itemIndex, 'hero_display_order', parseInt(value))}
                                    >
                                      <SelectTrigger className="w-32 bg-white border-gray-200 text-gray-900">
                                        <SelectValue placeholder="Select..." />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="1">1 (First)</SelectItem>
                                        <SelectItem value="2">2</SelectItem>
                                        <SelectItem value="3">3</SelectItem>
                                        <SelectItem value="4">4 (Last)</SelectItem>
                                      </SelectContent>
                                    </Select>
                                    <p className="text-gray-400 text-xs mt-1">Max 4 items per restaurant in carousel</p>
                                  </div>
                                )}
                              </div>

                              <div className="flex gap-2">
                                <Button
                                  onClick={() => handleSave(item)}
                                  className="bg-green-500 hover:bg-green-600 text-white"
                                  size="sm"
                                >
                                  <Save className="h-4 w-4 mr-1" />
                                  Save
                                </Button>
                                <Button
                                  onClick={() => handleCancel(item.menu_item_id)}
                                  className="bg-yellow-400 text-gray-900 hover:bg-yellow-300"
                                  size="sm"
                                >
                                  <X className="h-4 w-4 mr-1" />
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h3 className="text-lg font-semibold text-gray-900">
                                    {item.item_name}
                                  </h3>
                                  {item.availability === 'inactive' && (
                                    <span className="px-2 py-1 bg-orange-100 border border-orange-300 rounded text-xs font-medium text-orange-600">
                                      Out of Stock
                                    </span>
                                  )}
                                </div>
                                {item.item_description && (
                                  <p className="text-gray-600 text-sm mb-2">
                                    {item.item_description}
                                  </p>
                                )}
                                <p className="text-[#FDB913] font-semibold">
                                  ${item.primary_price.toFixed(2)}
                                </p>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  onClick={() => handleEdit(item.menu_item_id)}
                                  variant="ghost"
                                  className="text-green-400/60 hover:text-green-400 hover:bg-green-500/10"
                                  size="sm"
                                  title="Edit item"
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button
                                  onClick={() => setShowItemPreview(item.menu_item_id)}
                                  variant="ghost"
                                  className="text-blue-400/60 hover:text-blue-400 hover:bg-blue-500/10"
                                  size="sm"
                                  title="Preview item"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <label className={`inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 hover:bg-yellow-500/10 hover:text-yellow-400 h-8 w-8 text-yellow-400/60 ${uploadingImages.has(item.menu_item_id) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                                  title={uploadingImages.has(item.menu_item_id) ? "Uploading..." : "Upload image"}
                                >
                                  {uploadingImages.has(item.menu_item_id) ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-400" />
                                  ) : (
                                    <Upload className="h-4 w-4" />
                                  )}
                                  <input
                                    type="file"
                                    accept="image/*"
                                    disabled={uploadingImages.has(item.menu_item_id)}
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) handleImageUpload(item.menu_item_id, file);
                                    }}
                                    className="hidden"
                                  />
                                </label>
                                <div className="flex-1" /> {/* Spacer */}
                                <Button
                                  onClick={() => handleToggleAvailability(item.menu_item_id, item.availability || null)}
                                  variant="ghost"
                                  className={item.availability === 'inactive'
                                    ? "text-green-400/60 hover:text-green-400 hover:bg-green-500/10"
                                    : "text-orange-400/60 hover:text-orange-400 hover:bg-orange-500/10"}
                                  size="sm"
                                  disabled={togglingAvailability.has(item.menu_item_id)}
                                  title={item.availability === 'inactive' ? "Enable item (back in stock)" : "Disable item (out of stock)"}
                                >
                                  {togglingAvailability.has(item.menu_item_id) ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-400" />
                                  ) : item.availability === 'inactive' ? (
                                    <Power className="h-4 w-4" />
                                  ) : (
                                    <PowerOff className="h-4 w-4" />
                                  )}
                                </Button>
                                <Button
                                  onClick={() => handleDeleteItem(item.menu_item_id)}
                                  variant="ghost"
                                  className="text-red-400/60 hover:text-red-400 hover:bg-red-500/10"
                                  size="sm"
                                  disabled={deletingItems.has(item.menu_item_id)}
                                  title="Delete item"
                                >
                                  {deletingItems.has(item.menu_item_id) ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-400" />
                                  ) : (
                                    <Trash2 className="h-4 w-4" />
                                  )}
                                </Button>
                              </div>
                            </div>
                          )}
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {/* Sticky Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg p-4">
        <div className="container mx-auto max-w-6xl flex justify-between items-center">
          <p className="text-gray-500 text-sm">
            {menuItems.reduce((acc, section) => acc + section.items.length, 0)} items in your seasonal menu
          </p>
          <Button
            onClick={handleFinish}
            disabled={saving}
            className="bg-yellow-400 text-black hover:bg-yellow-300 font-bold px-8 py-3 rounded-full shadow-lg transform hover:scale-105 transition-all flex items-center gap-2"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
                Saving...
              </>
            ) : (
              <>
                <CheckCircle className="h-5 w-5" />
                Save & Finish
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Add Item Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Add Seasonal Menu Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Item Name *</label>
              <Input
                value={newItem.item_name}
                onChange={(e) => setNewItem({ ...newItem, item_name: e.target.value })}
                placeholder="Enter item name"
                className="mt-1 bg-white border-gray-200 text-gray-900 placeholder-gray-400"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Description</label>
              <Textarea
                value={newItem.item_description}
                onChange={(e) => setNewItem({ ...newItem, item_description: e.target.value })}
                placeholder="Enter item description"
                className="mt-1 bg-white border-gray-200 text-gray-900 placeholder-gray-400"
                rows={3}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Price *</label>
              <Input
                type="number"
                step="0.01"
                value={newItem.primary_price}
                onChange={(e) => setNewItem({ ...newItem, primary_price: e.target.value })}
                placeholder="0.00"
                className="mt-1 bg-white border-gray-200 text-gray-900 placeholder-gray-400"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Section *</label>
              <Select
                value={newItem.section}
                onValueChange={(value) => setNewItem({ ...newItem, section: value })}
              >
                <SelectTrigger className="mt-1 bg-white border-gray-200 text-gray-900">
                  <SelectValue placeholder="Select a section" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  {allSections.map((section) => (
                    <SelectItem key={section} value={section} className="text-gray-900 hover:bg-gray-50 focus:bg-gray-100">
                      {section}
                    </SelectItem>
                  ))}
                  <SelectItem value="__new__" className="text-gray-900 hover:bg-gray-50 focus:bg-gray-100">+ Create New Section</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Badge and Carousel Options */}
            <div className="col-span-2 pt-3 border-t border-gray-200">
              <label className="text-sm font-medium text-gray-700 block mb-3">Item Badges & Features</label>
              <div className="grid grid-cols-2 gap-3">
                {/* Popular Item Toggle */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="new-popular-toggle"
                    checked={newItem.is_popular}
                    onChange={(e) => setNewItem({ ...newItem, is_popular: e.target.checked })}
                    className="w-4 h-4 text-[#FDB913] bg-white border-gray-300 rounded focus:ring-[#FDB913] focus:ring-2"
                  />
                  <label htmlFor="new-popular-toggle" className="text-sm text-gray-700">
                    Popular Item
                  </label>
                </div>

                {/* Chef's Recommendation Toggle */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="new-chef-toggle"
                    checked={newItem.chef_reccom}
                    onChange={(e) => setNewItem({ ...newItem, chef_reccom: e.target.checked })}
                    className="w-4 h-4 text-[#FDB913] bg-white border-gray-300 rounded focus:ring-[#FDB913] focus:ring-2"
                  />
                  <label htmlFor="new-chef-toggle" className="text-sm text-gray-700">
                    Chef's Recommendation
                  </label>
                </div>

                {/* Hero Carousel Toggle */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="new-hero-toggle"
                    checked={newItem.is_hero_carousel}
                    onChange={(e) => setNewItem({ ...newItem, is_hero_carousel: e.target.checked })}
                    className="w-4 h-4 text-[#FDB913] bg-white border-gray-300 rounded focus:ring-[#FDB913] focus:ring-2"
                  />
                  <label htmlFor="new-hero-toggle" className="text-sm text-gray-700">
                    Hero Carousel (Mobile Landing)
                  </label>
                </div>

                {/* Hero Carousel Display Order - Only show when hero carousel is checked */}
                {newItem.is_hero_carousel && (
                  <div className="col-span-2">
                    <label className="text-gray-500 text-sm block mb-1">Display Order (1-4)</label>
                    <Select
                      value={newItem.hero_display_order?.toString() || ''}
                      onValueChange={(value) => setNewItem({ ...newItem, hero_display_order: parseInt(value) })}
                    >
                      <SelectTrigger className="w-32 bg-white border-gray-200 text-gray-900">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 (First)</SelectItem>
                        <SelectItem value="2">2</SelectItem>
                        <SelectItem value="3">3</SelectItem>
                        <SelectItem value="4">4 (Last)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-gray-400 text-xs mt-1">Max 4 items per restaurant in carousel</p>
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddModal(false);
                setNewItem({
                  item_name: '',
                  item_description: '',
                  primary_price: '10.00',
                  section: '',
                  is_popular: false,
                  chef_reccom: false,
                  is_hero_carousel: false,
                  hero_display_order: null
                });
              }}
              className="bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddItem}
              className="bg-[#FDB913] text-black hover:bg-[#e5a711] font-medium"
            >
              Add Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Item Confirmation Modal */}
      {itemToDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl"
          >
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Confirm Delete</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{itemToDelete.name}"? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setItemToDelete(null)}
                className="px-4 py-2 bg-gray-100 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-200 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all"
              >
                Delete
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Delete Image Confirmation Modal */}
      {imageToDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl"
          >
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Delete Image</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the image for "{imageToDelete.name}"? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setImageToDelete(null)}
                className="px-4 py-2 bg-gray-100 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-200 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteImage}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all"
              >
                Delete Image
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Item Preview Modal */}
      {showItemPreview && menuItems.flatMap(s => s.items).find(i => i.menu_item_id === showItemPreview) && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowItemPreview(null)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl max-w-3xl w-full overflow-hidden shadow-2xl border border-gray-200"
          >
            {(() => {
              const item = menuItems.flatMap(s => s.items).find(i => i.menu_item_id === showItemPreview)!;
              return (
                <>
                  <div className="relative aspect-video">
                    {item.uploaded_image_url || item.ai_image_url ? (
                      <img
                        src={item.uploaded_image_url || item.ai_image_url}
                        alt={item.item_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                        <Image className="h-24 w-24 text-gray-300" />
                      </div>
                    )}
                  </div>
                  <div className="p-8">
                    <h3 className="text-2xl font-semibold text-gray-900 mb-3">{item.item_name}</h3>
                    <p className="text-lg text-gray-600 mb-6 leading-relaxed">{item.item_description}</p>
                    <div className="flex justify-between items-center">
                      <span className="text-3xl font-bold text-[#FDB913]">${item.primary_price.toFixed(2)}</span>
                      <div className="flex gap-3">
                        {(item.uploaded_image_url || item.ai_image_url) && (
                          <button
                            onClick={() => {
                              setShowItemPreview(null);
                              handleDeleteImage(item.menu_item_id);
                            }}
                            disabled={deletingImages.has(item.menu_item_id)}
                            className={`px-5 py-2.5 bg-red-100 text-red-600 border border-red-300 rounded-lg hover:bg-red-200 transition-all font-medium text-base flex items-center gap-2 ${
                              deletingImages.has(item.menu_item_id) ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                          >
                            {deletingImages.has(item.menu_item_id) ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600" />
                                Deleting...
                              </>
                            ) : (
                              <>
                                <Trash2 className="h-4 w-4" />
                                Delete Image
                              </>
                            )}
                          </button>
                        )}
                        <button
                          onClick={() => setShowItemPreview(null)}
                          className="px-5 py-2.5 bg-gray-100 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-200 transition-all font-medium text-base"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              );
            })()}
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default SeasonalMenuItems;